import React, {
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { FixedSizeGrid as Grid } from "react-window";
import useResizeObserver from "use-resize-observer/polyfilled";
import "./ReportDataGrid.css";

// GIANT HACK: we need to do some gynastics to turn the grid
// from react-window into something with fixed column and row
// headers. Here's just some of the fun...

// Our headers just get what they need from props passed by the
// grid component. But the Cell isn't created by our code, so it
// needs to access a context directly (which should probably continue
// to be created here?)/
export const GridDataContext = createContext();

// This is part of the hack to make the sticky column and row headers work.
// After the main grid renders we pass the range down to our custom row
// and column headers via this context.
const ColumnRenderedContext = createContext();
const RowRenderedContext = createContext();

const GridConfigurationContext = createContext();

export default function ReportDataGrid({
    gridData,
    columnCount,
    columnWidth,
    rowHeight,
    rowCount,
    corner,
    rowHeader,
    itemsRenderedCallback
}) {
    const gridRef = useRef();

    const [columnRendered, setColumnRendered] = useState({
        colStart: 0,
        colStop: 0
    });

    const [rowRendered, setRowRendered] = useState({
        rowStart: 0,
        rowStop: 0
    });

    const onItemsRendered = useCallback(
        ({
            overscanColumnStartIndex: colStart,
            overscanRowStartIndex: rowStart,
            overscanColumnStopIndex: colStop,
            overscanRowStopIndex: rowStop
        }) => {
            setColumnRendered(
                prev =>
                    prev.colStart != colStart || prev.colStop != colStop
                        ? {
                              colStart,
                              colStop
                          }
                        : prev
            );
            setRowRendered(
                prev =>
                    prev.rowStart != rowStart || prev.rowStop != rowStop
                        ? {
                              rowStart,
                              rowStop
                          }
                        : prev
            );
            itemsRenderedCallback(colStart, rowStart, colStop, rowStop);
        },
        [itemsRenderedCallback]
    );

    const {
        ref: resizeRef,
        width: calculatedWidth = 0,
        height: calculatedHeight = 0
    } = useResizeObserver();

    useEffect(
        () => {
            gridRef.current &&
                gridRef.current.scrollTo({
                    scrollLeft: columnCount * columnWidth, //Number.MAX_SAFE_INTEGER,
                    scrollTop: 0
                });
        },
        [columnCount, columnWidth, gridRef.current]
    );

    const gridConfiguration = useMemo(
        () => ({
            columnWidth,
            rowHeight,
            corner,
            rowHeader
        }),
        [columnWidth, rowHeight, corner, rowHeader]
    );

    return (
        <div ref={resizeRef} className="report-data-grid-wrapper">
            <GridDataContext.Provider value={gridData}>
                <GridConfigurationContext.Provider value={gridConfiguration}>
                    <RowRenderedContext.Provider value={rowRendered}>
                        <ColumnRenderedContext.Provider value={columnRendered}>
                            {calculatedHeight && (
                                <Grid
                                    ref={gridRef}
                                    className="report-data-grid"
                                    onItemsRendered={onItemsRendered}
                                    outerElementType={GridOuterElement}
                                    innerElementType={GridInnerElement}
                                    //useIsScrolling
                                    overscanColumnCount={30}
                                    overscanRowCount={20}
                                    columnCount={columnCount}
                                    rowCount={rowCount}
                                    columnWidth={columnWidth}
                                    rowHeight={rowHeight}
                                    width={calculatedWidth}
                                    height={calculatedHeight}
                                >
                                    {ContextCell}
                                </Grid>
                            )}
                        </ColumnRenderedContext.Provider>
                    </RowRenderedContext.Provider>
                </GridConfigurationContext.Provider>
            </GridDataContext.Provider>
        </div>
    );
}

// Right now there's no reason for this to know about the grid re-rendering
const CornerWrapper = React.memo(() => {
    const { corner: Corner, columnWidth, rowHeight } = useContext(GridConfigurationContext);
    return (
        <div className="corner">
            <Corner columnWidth={columnWidth} rowHeight={rowHeight} />
        </div>
    );
});
CornerWrapper.displayName = "CornerWrapper";

const ColumnHeaderWrapper = React.memo(() => {
    const { columnWidth, rowHeight } = useContext(GridConfigurationContext);
    const { colStart, colStop } = useContext(ColumnRenderedContext);
    return (
        <div className="column-header">
            <ContextColumnHeader
                colStart={colStart}
                colStop={colStop}
                columnWidth={columnWidth}
                rowHeight={rowHeight}
            />
        </div>
    );
});

const RowHeaderWrapper = React.memo(() => {
    const { rowHeader: RowHeader, columnWidth, rowHeight } = useContext(GridConfigurationContext);
    const { rowStart, rowStop } = useContext(RowRenderedContext);
    return (
        <div className="row-header">
            <RowHeader
                rowStart={rowStart}
                rowStop={rowStop}
                columnWidth={columnWidth}
                rowHeight={rowHeight}
            />
        </div>
    );
});

const GridOuterElement = forwardRef(({ children, ...rest }, ref) => (
    <div ref={ref} {...rest}>
        <CornerWrapper />
        <ColumnHeaderWrapper />
        <RowHeaderWrapper />
        {children}
    </div>
));

const GridInnerElement = forwardRef(({ children, ...rest }, ref) => (
    <div className="inner" ref={ref} {...rest}>
        {children}
    </div>
));

const ContextCell = React.memo(({ columnIndex, rowIndex, style }) => {
    const { cellValue } = useContext(GridDataContext);
    const value = cellValue(columnIndex, rowIndex);

    return (
        <div className="cell" style={style}>
            {value}
        </div>
    );
});

const ContextColumnHeader = React.memo(({ colStart, colStop, columnWidth, rowHeight }) => {
    const { periodHeader, totalHeader } = useContext(GridDataContext);

    return (
        <>
            {Array.from({ length: colStop - colStart + 1 }, (_, i) => {
                const index = colStart + i;
                const value = periodHeader(index);
                return (
                    <div
                        key={index}
                        className="h1"
                        style={{
                            position: "absolute",
                            height: rowHeight,
                            width: columnWidth,
                            top: 0,
                            left: columnWidth * index
                        }}
                    >
                        {value}
                    </div>
                );
            })}
            {Array.from({ length: colStop - colStart + 1 }, (_, i) => {
                const index = colStart + i;
                const value = totalHeader(index);
                return (
                    <div
                        key={index}
                        className="h2 bold"
                        style={{
                            position: "absolute",
                            height: rowHeight,
                            width: columnWidth,
                            top: rowHeight,
                            left: columnWidth * index
                        }}
                    >
                        {value}
                    </div>
                );
            })}
        </>
    );
});
